"use client";

import { Button, Heading } from "@infinaut/ui";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@infinaut/ui/client";
import { Car, Menu } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";
import { handleScrollToId, handleScrollToTop } from "@/src/app/utills";

// ============================================================================
// Constants
// ============================================================================

const CONTACT_SECTION_ID = "contact";
const CTA_TEXT = "무료 견적 신청하기";

// ============================================================================
// Types
// ============================================================================

interface NavLink {
	name: string;
	id: string;
}

interface NavbarClientProps {
	navLinks: NavLink[];
}

interface NavLinkButtonProps {
	idx: number;
	link: NavLink;
	onClose?: () => void;
}

// ============================================================================
// Subcomponents
// ============================================================================

const NavLinkButton: React.FC<NavLinkButtonProps> = ({
	idx,
	link,
	onClose,
}) => {
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (idx === 0) {
				handleScrollToTop();
			} else {
				handleScrollToId(e, link.id);
			}
			onClose?.();
		},
		[idx, link.id, onClose],
	);

	return (
		<Button
			variant="link"
			onClick={handleClick}
			className="justify-start text-lg text-secondary p-0 hover:cursor-pointer transition-colors"
			aria-label={`${link.name} 섹션으로 이동`}
		>
			{link.name}
		</Button>
	);
};

const CTA: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			handleScrollToId(e, CONTACT_SECTION_ID);
			onClose?.();
		},
		[onClose],
	);

	return (
		<Button
			className="w-fit font-bold hover:cursor-pointer transition-all"
			onClick={handleClick}
			aria-label={CTA_TEXT}
		>
			{CTA_TEXT}
		</Button>
	);
};

const Logo: React.FC = () => {
	return (
		<Button
			className="flex items-center gap-2 cursor-pointer bg-transparent hover:bg-transparent transition-opacity"
			onClick={handleScrollToTop}
			aria-label="페이지 상단으로 이동"
		>
			<div className="bg-primary p-1.5 rounded-lg flex items-center justify-center">
				<Car className="text-primary-foreground h-6 w-6" />
			</div>
			<Heading
				level={4}
				className="font-black text-foreground tracking-tighter hidden sm:block"
			>
				프라임<span className="text-primary">렌트카</span>
			</Heading>
		</Button>
	);
};

const DesktopMenu: React.FC<{ navLinks: NavLink[] }> = ({ navLinks }) => (
	<div className="hidden md:flex items-center gap-6 lg:gap-8">
		{navLinks.map((link, idx) => (
			<NavLinkButton idx={idx} key={link.id} link={link} />
		))}
		<div className="ml-4 pl-4 border-l border-border">
			<CTA />
		</div>
	</div>
);

const MobileMenu: React.FC<{
	navLinks: NavLink[];
}> = ({ navLinks }) => {
	const [open, setOpen] = useState(false);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, []);

	return (
		<div className="md:hidden">
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						aria-label="메뉴 열기"
						className="hover:bg-accent"
					>
						<Menu className="h-5 w-5" />
					</Button>
				</SheetTrigger>
				<SheetContent side="right" className="border-sidebar-border w-64 px-4">
					<SheetTitle className="text-lg font-semibold py-3 border-b border-border">
						메뉴
					</SheetTitle>
					<div className="flex flex-col gap-4">
						{navLinks.map((link, idx) => (
							<NavLinkButton
								idx={idx}
								key={link.id}
								link={link}
								onClose={handleClose}
							/>
						))}
						<div className="pt-4 border-t border-border">
							<CTA onClose={handleClose} />
						</div>
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
};

// ============================================================================
// Main Component
// ============================================================================

const NavbarClient: React.FC<NavbarClientProps> = ({ navLinks }) => {
	return (
		<nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background/95 backdrop-blur-md shadow-lg py-3">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-16">
					<Logo />
					<DesktopMenu navLinks={navLinks} />
					<MobileMenu navLinks={navLinks} />
				</div>
			</div>
		</nav>
	);
};

export default NavbarClient;
